import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { ExperienceCrossSell } from '../components/ExperienceCrossSell';
import { FlightResultCard } from '../components/FlightResultCard';
import { NearbyDatesStrip } from '../components/NearbyDatesStrip';
import { MonthPriceCalendar } from '../components/MonthPriceCalendar';
import { EsimCrossSell } from '../components/EsimCrossSell';
import { TripCrossSell } from '../components/TripCrossSell';
import { FilterControls } from '../components/FilterControls';
import { DestinationContextPanel } from '../components/DestinationContextPanel';
import { AffiliateDisclosure } from '../components/AffiliateDisclosure';
import { FlightSearchBar } from '../components/FlightSearchBar';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Drawer, DrawerContent, DrawerTrigger, DrawerTitle, DrawerHeader } from '../components/ui/drawer';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { openDeal } from '../lib/deals';
import { fmtDateISO } from '../lib/dateFormat';
import { toast } from 'sonner';
import { SmartFareCard, SmartFareDetail } from '../components/SmartFare';
import { SearchProgress } from '../components/SearchProgress';
import { SlidersHorizontal, ArrowUpDown, Bell, PlaneTakeoff, Sparkles } from 'lucide-react';

const SORTS = [
  { v: 'best', l: 'Best value' },
  { v: 'cheapest', l: 'Cheapest' },
  { v: 'fastest', l: 'Fastest' },
];

export default function FlightResults() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { currency, promptEsim } = useApp();
  const { user, requireAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [context, setContext] = useState(null);
  const [sort, setSort] = useState(params.get('sort') || 'cheapest');
  const [savedIds, setSavedIds] = useState(new Set());
  const [smartFares, setSmartFares] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [sfDetail, setSfDetail] = useState(null);

  const origin = params.get('origin');
  const destination = params.get('destination');
  const mcRaw = params.get('mc');
  const directions = useMemo(() => {
    if (!mcRaw) return null;
    try { const d = JSON.parse(mcRaw); return Array.isArray(d) && d.length >= 2 ? d : null; } catch { return null; }
  }, [mcRaw]);
  const isMultiCity = !!directions;

  const [filters, setFilters] = useState(null);
  const [bounds, setBounds] = useState(null);

  // Persist sort/filter state to the URL (shareable & refresh-safe) WITHOUT retriggering
  // a new provider search (search only re-runs when the search params below change).
  const writeUrl = useCallback((patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => { if (v === '' || v == null) next.delete(k); else next.set(k, v); });
    setParams(next, { replace: true });
  }, [params, setParams]);

  const readFiltersFromUrl = (b) => {
    const clamp = (v, lo, hi, d) => { const n = (v == null || v === '') ? NaN : Number(v); return isFinite(n) ? Math.min(hi, Math.max(lo, n)) : d; };
    return {
      maxPrice: clamp(params.get('fp'), b.priceMin, b.priceMax, b.priceMax),
      maxDuration: clamp(params.get('fd'), b.durMin, b.durMax, b.durMax),
      stops: (params.get('fs') || '').split(',').filter((x) => x !== '').map(Number),
      airlines: (params.get('fa') || '').split(',').filter(Boolean),
      baggageOnly: params.get('fb') === '1',
    };
  };

  const [streaming, setStreaming] = useState(false);
  const [showCount, setShowCount] = useState(8);
  const [searchCtx, setSearchCtx] = useState(null);
  const streamAbortRef = React.useRef(null);
  useEffect(() => () => { streamAbortRef.current?.abort(); }, []);

  const runSearch = useCallback(async () => {
    streamAbortRef.current?.abort();
    const ac = new AbortController();
    streamAbortRef.current = ac;
    setLoading(true);
    setStreaming(true);
    setShowCount(8);
    setSearchCtx(null);
    setData(null);
    setContext(null);
    const body = isMultiCity ? {
      trip_type: 'multi_city', directions,
      adults: Number(params.get('adults') || 1),
      children: Number(params.get('children') || 0),
      cabin: params.get('cabin') || 'economy',
      currency,
    } : {
      origin, destination,
      trip_type: params.get('trip_type') || 'round_trip',
      depart_date: params.get('depart_date') || null,
      return_date: params.get('return_date') || null,
      adults: Number(params.get('adults') || 1),
      children: Number(params.get('children') || 0),
      cabin: params.get('cabin') || 'economy',
      currency,
    };
    // User-tuned filters are respected; otherwise filters track the (growing) result set as
    // fares stream in, so the default view always shows everything found so far.
    const userFiltered = ['fp', 'fd', 'fs', 'fa', 'fb'].some((k) => params.get(k));
    let sessionId = null;
    const applyResults = (res) => {
      setData(res);
      const prices = res.offers.map((o) => o.price_inr).filter((p) => p > 0);
      const durs = res.offers.map((o) => o.total_duration_minutes ?? o.duration_minutes).filter(Boolean);
      if (prices.length) {
        const b = {
          priceMin: Math.floor(Math.min(...prices)), priceMax: Math.ceil(Math.max(...prices)),
          durMin: Math.min(...durs), durMax: Math.max(...durs),
        };
        setBounds(b);
        if (!userFiltered) setFilters(readFiltersFromUrl(b));
        else setFilters((prev) => prev || readFiltersFromUrl(b));
      }
    };
    try {
      // All-airports mode: origin or destination carries multiple codes (e.g. "GOI,GOX").
      // Streamed just like single-airport — fares appear progressively as each pair lands.
      const isAllAirports = !isMultiCity && (origin.includes(',') || destination.includes(','));
      const streamUrl = isAllAirports ? api.multiFlightStreamUrl() : api.flightStreamUrl();
      const streamBody = isAllAirports ? {
        origins: origin.split(','), destinations: destination.split(','),
        trip_type: body.trip_type, depart_date: body.depart_date, return_date: body.return_date,
        adults: body.adults, children: body.children, cabin: body.cabin, currency,
      } : body;
      const resp = await fetch(streamUrl, {
        method: 'POST', credentials: 'include', signal: ac.signal,
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(streamBody),
      });
      if (!resp.ok || !resp.body) throw new Error('stream failed');
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      // Throttle heavy list re-renders so the edit search bar stays responsive while streaming.
      let lastApplyTs = 0;
      let pendingUpdate = null;
      const flushPending = () => {
        if (pendingUpdate) { applyResults(pendingUpdate); pendingUpdate = null; lastApplyTs = Date.now(); }
      };
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (ac.signal.aborted) return;
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let nl;
        while ((nl = buf.indexOf('\n')) >= 0) {
          const line = buf.slice(0, nl).trim();
          buf = buf.slice(nl + 1);
          if (!line) continue;
          let msg; try { msg = JSON.parse(line); } catch { continue; }
          if (msg.type === 'meta') {
            sessionId = msg.session_id;
            setSearchCtx(msg.context || null);
            setData({ session_id: sessionId, context: msg.context, offers: [], count: 0, is_mock: false, provider: '' });
          } else if (msg.type === 'update') {
            const payload = { session_id: sessionId, offers: msg.offers, count: msg.count, is_mock: msg.is_mock, provider: msg.provider };
            if (msg.offers.length > 0 || msg.is_over) setLoading(false);
            const now = Date.now();
            if (now - lastApplyTs >= 700) { applyResults(payload); lastApplyTs = now; pendingUpdate = null; }
            else { pendingUpdate = payload; }
          } else if (msg.type === 'done') {
            flushPending();
            setStreaming(false);
            if (sessionId) {
              api.track('flight_result_viewed', sessionId, { count: msg.count });
              api.context(sessionId).then(({ data: ctx }) => setContext(ctx)).catch(() => {});
              setSmartFares([]);
              api.smartFares(sessionId).then(({ data: sf }) => setSmartFares(sf.smart_fares || [])).catch(() => setSmartFares([]));
            }
          }
        }
      }
      flushPending();
    } catch (e) {
      if (e?.name === 'AbortError' || ac.signal.aborted) return; // superseded by a newer search
      toast.error('Search failed. Please try again.');
    } finally {
      if (!ac.signal.aborted) {
        setLoading(false);
        setStreaming(false);
      }
    }
  }, [origin, destination, params, currency, isMultiCity, directions]);

  const searchKey = isMultiCity
    ? `mc|${mcRaw}|${params.get('adults') || ''}|${params.get('children') || ''}|${params.get('cabin') || ''}`
    : ['origin', 'destination', 'trip_type', 'depart_date', 'return_date', 'adults', 'children', 'cabin'].map((k) => params.get(k) || '').join('|');

  useEffect(() => {
    if (!isMultiCity && (!origin || !destination)) { navigate('/flights'); return; }
    document.title = isMultiCity
      ? `Multi-city flights | FlightDeals.in`
      : `${origin} → ${destination} flights | FlightDeals.in`;
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKey]);

  const airlines = useMemo(() => {
    if (!data) return [];
    const map = {};
    data.offers.forEach((o) => { map[o.airline_code] = o.airline_name; });
    return Object.entries(map).map(([code, name]) => ({ code, name }));
  }, [data]);

  const visible = useMemo(() => {
    if (!data || !filters) return [];
    const dur = (o) => o.total_duration_minutes ?? o.duration_minutes;
    let list = data.offers.filter((o) =>
      o.price_inr <= filters.maxPrice &&
      dur(o) <= filters.maxDuration &&
      (filters.stops.length === 0 || filters.stops.includes(Math.min(o.stops, 2))) &&
      (filters.airlines.length === 0 || filters.airlines.includes(o.airline_code)) &&
      (!filters.baggageOnly || o.baggage_checked_kg > 0)
    );
    if (sort === 'cheapest') list = [...list].sort((a, b) => a.price_inr - b.price_inr);
    else if (sort === 'fastest') list = [...list].sort((a, b) => dur(a) - dur(b));
    else list = [...list].sort((a, b) => b.score - a.score);
    return list;
  }, [data, filters, sort]);

  const onSort = (v) => { setSort(v); setShowCount(8); writeUrl({ sort: v === 'cheapest' ? '' : v }); api.track('sort_used', data?.session_id, { sort: v }); };
  const onFilterChange = (next) => {
    if (next && bounds) {
      setShowCount(8);
      writeUrl({
        fp: next.maxPrice < bounds.priceMax ? String(next.maxPrice) : '',
        fd: next.maxDuration < bounds.durMax ? String(next.maxDuration) : '',
        fs: next.stops.length ? next.stops.join(',') : '',
        fa: next.airlines.length ? next.airlines.join(',') : '',
        fb: next.baggageOnly ? '1' : '',
      });
    }
    api.track('filter_used', data?.session_id, {});
  };

  const onViewDeal = (offer, proposalId) => {
    const crossSell = () => promptEsim({ country_code: searchCtx?.destination_country_code, country: searchCtx?.destination_city, city: searchCtx?.destination_city });
    // Real-time (tickets-api) offers: fetch the affiliate booking URL on click via the
    // backend, which maps session -> Aviasales search_id and 302s to the agency page.
    if (offer.buy_kind === 'tickets' && data?.session_id && (proposalId || offer.proposal_id)) {
      const pid = proposalId || offer.proposal_id;
      const win = window.open('about:blank', '_blank');
      const url = api.flightBuyUrl(data.session_id, pid);
      if (win) win.location.href = url; else window.location.href = url;
      api.track('flight_buy_click', data.session_id, { proposal_id: pid, offer_id: offer.offer_id });
      crossSell();
      return;
    }
    openDeal({ type: 'flight', offer, sessionId: data.session_id, surface: 'flight_results', source: '/flights/results' });
    crossSell();
  };

  const openSfDetail = (o) => { setSfDetail(o); api.trackSmartFare('smart_fare_open', o.id, data?.session_id).catch(() => {}); };
  const onSfContinue = (o) => {
    setSfDetail(null);
    // Hidden-city (and other opps found via their own live search) book through the
    // opportunity's stored search/proposal, not the current results session.
    if (o.type === 'INTERMEDIATE_DESTINATION' || o.provider_results_url) {
      const win = window.open('about:blank', '_blank');
      const url = api.smartFareBuyUrl(o.id);
      if (win) win.location.href = url; else window.location.href = url;
      api.trackSmartFare('smart_fare_partner_click', o.id, data?.session_id, { type: o.type }).catch(() => {});
      return;
    }
    const underlying = (data?.offers || []).find((x) => x.offer_id === o.offer_id);
    if (underlying) openDeal({ type: 'flight', offer: underlying, sessionId: data.session_id, surface: 'flight_results_smartfare', source: '/flights/results' });
    else toast.info('Fare details shown. Availability is checked on the partner site.');
  };

  const onSave = (offer) => {
    requireAuth(async () => {
      try {
        await api.saveItem({ item_type: 'flight', title: `${offer.airline_name} ${offer.origin}→${offer.destination}`, data: offer });
        setSavedIds((s) => new Set(s).add(offer.offer_id));
        toast.success('Saved to your account');
      } catch { toast.error('Could not save'); }
    });
  };

  return (
    <Layout>
      <div className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3 min-w-0 mb-3">
            <PlaneTakeoff className="h-5 w-5 text-accent shrink-0" />
            <div className="min-w-0">
              <p className="font-semibold truncate" data-testid="results-route-summary">
                {isMultiCity
                  ? directions.map((d) => d.origin).concat(directions[directions.length - 1].destination).join(' → ')
                  : `${searchCtx?.origin_city || context?.context?.origin_city || origin} → ${searchCtx?.destination_city || context?.context?.destination_city || destination}`}
              </p>
              <p className="text-xs text-primary-foreground/70">
                {isMultiCity
                  ? `${directions.length} flights · ${params.get('adults') || 1} traveller(s) · ${(params.get('cabin') || 'economy').replace('_', ' ')}`
                  : `${fmtDateISO(params.get('depart_date'))}${params.get('return_date') ? ` – ${fmtDateISO(params.get('return_date'))}` : ''} · ${params.get('adults') || 1} traveller(s) · ${(params.get('cabin') || 'economy').replace('_', ' ')}`}
              </p>
            </div>
          </div>
          {/* Always-visible editable search bar — tweak sectors/dates/pax and re-search inline. */}
          <div data-testid="results-edit-bar">
            <FlightSearchBar
              key={`${origin}-${destination}-${params.get('depart_date')}-${params.get('return_date')}-${params.get('trip_type')}-${searchCtx?.origin_city || ''}-${searchCtx?.destination_city || ''}`}
              initial={{
                trip_type: params.get('trip_type') || 'round_trip',
                from: { code: origin, city: searchCtx?.origin_city || context?.context?.origin_city || origin },
                to: { code: destination, city: searchCtx?.destination_city || context?.context?.destination_city || destination },
                depart_date: params.get('depart_date') || undefined,
                return_date: params.get('return_date') || undefined,
                adults: Number(params.get('adults') || 1),
                children: Number(params.get('children') || 0),
                cabin: params.get('cabin') || 'economy',
              }}
              onSearch={(p) => {
                if (p.trip_type === 'multi_city') {
                  const q = new URLSearchParams({ trip_type: 'multi_city', mc: JSON.stringify(p.directions), adults: p.adults, children: p.children, cabin: p.cabin }).toString();
                  navigate(`/flights/results?${q}`);
                  return;
                }
                const q = new URLSearchParams({ origin: p.origin, destination: p.destination, trip_type: p.trip_type, depart_date: p.depart_date, return_date: p.return_date || '', adults: p.adults, children: p.children, cabin: p.cabin }).toString();
                navigate(`/flights/results?${q}`);
              }}
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-4"><AffiliateDisclosure variant="banner" /></div>

        {/* Sort + mobile controls */}
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar" data-testid="sort-chips">
            {SORTS.map((s) => (
              <button key={s.v} onClick={() => onSort(s.v)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border transition-colors ${sort === s.v ? 'bg-accent/12 text-accent border-accent/30' : 'bg-card text-muted-foreground border-border hover:bg-secondary'}`}
                data-testid={`sort-${s.v}`}>{s.l}</button>
            ))}
            {smartFares.length > 0 && (
              <button onClick={() => onSort('smart')}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap border transition-colors inline-flex items-center gap-1 ${sort === 'smart' ? 'bg-accent text-accent-foreground border-accent' : 'bg-accent/10 text-accent border-accent/30 hover:bg-accent/20'}`}
                data-testid="sort-smart"><Sparkles className="h-3.5 w-3.5" /> Smart Fares ({smartFares.length})</button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {user && data && !isMultiCity && (
              <PriceAlertDialog origin={origin} destination={destination} depart={params.get('depart_date')} currency={currency} minPrice={bounds?.priceMin} />
            )}
            <Drawer>
              <DrawerTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-xl lg:hidden" data-testid="open-filters-button">
                  <SlidersHorizontal className="h-4 w-4 mr-1.5" /> Filters
                </Button>
              </DrawerTrigger>
              <DrawerContent data-testid="filters-bottom-sheet">
                <DrawerHeader><DrawerTitle>Filters</DrawerTitle></DrawerHeader>
                <div className="px-4 pb-8 overflow-auto">
                  {filters && bounds && <FilterControls filters={filters} setFilters={setFilters} bounds={bounds} airlines={airlines} currency={currency} onChange={onFilterChange} />}
                </div>
              </DrawerContent>
            </Drawer>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[260px_1fr_300px]">
          {/* Desktop filters */}
          <aside className="hidden lg:block">
            <div className="sticky top-20 rounded-2xl border border-border bg-card p-5">
              <h3 className="font-semibold mb-4 flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" /> Filters</h3>
              {filters && bounds && <FilterControls filters={filters} setFilters={setFilters} bounds={bounds} airlines={airlines} currency={currency} onChange={onFilterChange} />}
            </div>
          </aside>

          {/* Results */}
          <div className="min-w-0">
            <SearchProgress
              active={loading || streaming}
              value={loading && !(data?.offers?.length) ? 14 : Math.min(96, 20 + (data?.offers?.length || 0))}
              label={streaming ? `Finding more live fares… ${data?.offers?.length || 0} found` : (loading ? 'Searching live fares across airlines & agencies…' : '')}
              testid="flight-search-progress"
            />
            {loading && (
              <div className="space-y-3" data-testid="flight-results-loading">
                <div className="flex items-center gap-3 rounded-2xl border border-accent/30 bg-accent/[0.04] px-4 py-3">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent/60" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-accent" />
                  </span>
                  <p className="text-sm font-medium">Searching live fares across hundreds of airlines & agencies… this can take a few seconds.</p>
                </div>
                {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}
              </div>
            )}
            {!loading && data && sort === 'smart' && (
              <>
                <p className="text-sm text-muted-foreground mb-3">
                  <span className="font-semibold text-foreground">{smartFares.length}</span> Smart Fare {smartFares.length === 1 ? 'opportunity' : 'opportunities'}
                  <span className="ml-2 text-xs bg-accent/10 text-accent px-2 py-0.5 rounded">Live fare intelligence</span>
                </p>
                <div className="space-y-3" data-testid="smart-fares-list">
                  {smartFares.map((o) => (
                    <SmartFareCard key={o.id} o={o} currency={currency} sessionId={data.session_id} onDetail={openSfDetail} />
                  ))}
                  {smartFares.length === 0 && (
                    <div className="text-center py-16 rounded-2xl border border-dashed border-border" data-testid="smart-fares-empty">
                      <p className="font-medium">No smarter fare found for this search</p>
                      <p className="text-sm text-muted-foreground">The default results are already the best available live fares.</p>
                    </div>
                  )}
                </div>
              </>
            )}
            {!loading && data && sort !== 'smart' && (
              <>
                {!isMultiCity && (
                  <NearbyDatesStrip
                    origin={origin} destination={destination}
                    date={params.get('depart_date')}
                    roundTrip={(params.get('trip_type') || 'round_trip') === 'round_trip'}
                    currency={currency}
                    onPick={(d) => {
                      const next = new URLSearchParams(params);
                      next.set('depart_date', d);
                      const rt = (params.get('trip_type') || 'round_trip') === 'round_trip';
                      const cur = params.get('return_date');
                      if (rt && cur && new Date(cur) <= new Date(d)) {
                        const nd = new Date(d); nd.setDate(nd.getDate() + 7);
                        next.set('return_date', nd.toISOString().slice(0, 10));
                      }
                      setParams(next);
                    }}
                  />
                )}
                {!isMultiCity && origin && destination && (
                  <div className="mb-4">
                    <button
                      type="button"
                      onClick={() => setShowCalendar((v) => !v)}
                      data-testid="flexible-dates-toggle"
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline mb-2"
                    >
                      <PlaneTakeoff className="h-4 w-4" />
                      {showCalendar ? 'Hide full-month calendar' : 'See a whole month of prices'}
                    </button>
                    {showCalendar && (
                      <MonthPriceCalendar
                        origin={origin} destination={destination}
                        roundTrip={(params.get('trip_type') || 'round_trip') === 'round_trip'}
                        currency={currency}
                        initialMonth={(params.get('depart_date') || '').slice(0, 7) || undefined}
                        selectedDate={params.get('depart_date')}
                        onPick={(d) => {
                          const next = new URLSearchParams(params);
                          next.set('depart_date', d);
                          const rt = (params.get('trip_type') || 'round_trip') === 'round_trip';
                          const cur = params.get('return_date');
                          if (rt && cur) {
                            // preserve the traveller's trip length (nights) from the new depart date
                            const oldDep = params.get('depart_date');
                            let nights = 7;
                            if (oldDep) {
                              const diff = Math.round((new Date(cur) - new Date(oldDep)) / 86400000);
                              if (diff > 0) nights = diff;
                            }
                            const nd = new Date(d); nd.setDate(nd.getDate() + nights);
                            next.set('return_date', nd.toISOString().slice(0, 10));
                          }
                          setParams(next);
                          toast.success(`Searching ${origin} → ${destination} for ${d}`);
                        }}
                      />
                    )}
                  </div>
                )}
                <p className="text-sm text-muted-foreground mb-3 flex items-center gap-2">
                  <span>Showing <span className="font-semibold text-foreground">{Math.min(showCount, visible.length)}</span> of {visible.length} flights</span>
                  {streaming && (
                    <span className="inline-flex items-center gap-1.5 text-accent font-medium" data-testid="live-updating-indicator">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent/60" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
                      </span>
                      finding more live fares…
                    </span>
                  )}
                  {data.is_mock && <span className="ml-1 text-xs bg-secondary px-2 py-0.5 rounded">demo data</span>}
                </p>
                {!isMultiCity && searchCtx?.destination_country_code && (
                  <EsimCrossSell countryCode={searchCtx.destination_country_code} destinationCity={searchCtx.destination_city} currency={currency} />
                )}
                {!isMultiCity && searchCtx?.destination_city && (
                  <TripCrossSell destinationCity={searchCtx.destination_city} destinationCountry={searchCtx.destination_country} destinationCountryCode={searchCtx.destination_country_code} destinationCode={searchCtx.destination_code}
                    departDate={params.get('depart_date')} returnDate={params.get('return_date')}
                    adults={Number(params.get('adults') || 1)} children={Number(params.get('children') || 0)} />
                )}
                <div className="space-y-3" data-testid="flight-results-list">
                  {visible.slice(0, showCount).map((o) => (
                    <div key={o.offer_id}>
                      <FlightResultCard offer={o} currency={currency} onViewDeal={onViewDeal} onSave={onSave} saved={savedIds.has(o.offer_id)} />
                    </div>
                  ))}
                  {visible.length === 0 && (
                    <div className="text-center py-16 rounded-2xl border border-dashed border-border">
                      <p className="font-medium">No flights match your filters</p>
                      <p className="text-sm text-muted-foreground">Try widening your price or duration.</p>
                    </div>
                  )}
                  {visible.length > showCount && (
                    <button
                      onClick={() => setShowCount((c) => c + 8)}
                      className="w-full h-12 rounded-2xl border border-accent/40 bg-accent/[0.05] text-accent font-semibold hover:bg-accent/10 transition-colors"
                      data-testid="show-more-flights">
                      Show more results ({visible.length - showCount} more)
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Cross-sell */}
          <aside className="lg:block">
            <div className="sticky top-20">
              {context && <DestinationContextPanel context={context.context} modules={context.modules} />}
            </div>
          </aside>
        </div>
        <ExperienceCrossSell city={context?.context?.destination_city || ''} code={destination}
          currency={currency} source="/flights/results" />
      </div>
      <SmartFareDetail o={sfDetail} currency={currency} sessionId={data?.session_id} open={!!sfDetail} onClose={() => setSfDetail(null)} onContinue={onSfContinue} />
    </Layout>
  );
}

function PriceAlertDialog({ origin, destination, depart, currency, minPrice }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(minPrice ? Math.round(minPrice * 0.9) : 20000);
  useEffect(() => { if (minPrice) setTarget(Math.round(minPrice * 0.9)); }, [minPrice]);
  const create = async () => {
    try {
      await api.createAlert({ origin, destination, depart_date: depart, target_price: Number(target), currency: 'INR' });
      toast.success('Price alert created — see it in your account');
      setOpen(false);
    } catch { toast.error('Could not create alert'); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-xl" data-testid="track-price-button"><Bell className="h-4 w-4 mr-1.5" /> Track price</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Alert me when the price drops</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">We'll track {origin} → {destination} and notify you when a fare hits your target.</p>
        <div className="space-y-2">
          <Label>Target price (INR)</Label>
          <Input type="number" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="target-price-input" />
        </div>
        <DialogFooter>
          <Button onClick={create} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="create-alert-button">Create alert</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
